import { getGaCookieValue } from 'analytics/utils/getGaCookieValue/getGaCookieValue'
import { userAgent as getUserAgent } from 'next/server'
import type { Session } from 'session/src/types/session'
import { env } from 'utils/envManager'
import type { AIResponse, ConversationTrigger } from '../types/AIResponseTypes'

export interface ResponseMessage {
	payload: AIResponse
}

export interface ChatbotDispatcherResponse {
	queryResult: {
		responseMessages: ResponseMessage[]
	}
	info: {
		operator: boolean
	}
}

interface ChatbotDispatcherFetcherOptions {
	languageISO: string
	timeZone: string
	countryISO: string
	isOutlet: boolean
	sessionId: string | null
	token: string | null
	signal: AbortSignal
	message?: string
	conversationTrigger?: ConversationTrigger
	externalId?: Session['externalId']
}

interface GetQueryInputProps {
	languageISO: string
	message?: string
	conversationTrigger?: ConversationTrigger
}

interface GetQueryInputEvent {
	event: {
		name: string
		languageCode: string
	}
}

interface GetQueryInputText {
	text: {
		text?: string
		languageCode: string
	}
}

type GetQueryInput = GetQueryInputEvent | GetQueryInputText

const SOURCE = 'chat'

const getQueryInput = ({
	message,
	languageISO,
	conversationTrigger,
}: GetQueryInputProps): GetQueryInput => {
	if (conversationTrigger) {
		return {
			event: {
				name: conversationTrigger,
				languageCode: languageISO,
			},
		}
	}

	return {
		text: {
			text: message,
			languageCode: languageISO,
		},
	}
}
export const chatBotGetAIResponse = async (
	url: URL,
	{
		message,
		languageISO,
		timeZone,
		sessionId,
		countryISO,
		isOutlet,
		token,
		signal,
		conversationTrigger,
		externalId = null,
	}: ChatbotDispatcherFetcherOptions
): Promise<ChatbotDispatcherResponse> => {
	const userAgent = getUserAgent({
		headers: {
			get: () => window.navigator.userAgent,
		} as unknown as Headers,
	})

	return (
		await fetch(url.href, {
			next: { revalidate: 0 },
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'x-fa': env.NEXT_PUBLIC_FASHION_ASSISTANT_HEADER,
				Authorization: `Bearer ${token}`,
				xfrom: 'mango',
			},
			body: JSON.stringify({
				queryInput: getQueryInput({
					message,
					languageISO,
					conversationTrigger,
				}),
				queryParams: {
					timeZone,
					sessionID: sessionId,
					payload: { source: SOURCE },
					customParams: {
						isOutlet,
						externalId,
						source: SOURCE,
						languageCode: languageISO,
						countryCode: countryISO,
						isConversational: true,
						isLiveChatQueue: false,
						userPseudoId: getGaCookieValue(),
						gaSessionId: window.analytics.sessionId,
						os: userAgent.os.name,
					},
				},
			}),
			signal,
		})
	).json()
}
